import { FC, MouseEvent } from "react";
import styled, { useTheme } from "styled-components";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useFieldArray, useForm } from "react-hook-form";

import {
  Actions,
  CloseButton,
  StyledDialog,
} from "@/components/AlertDialogs/Dialog";
import { Modal } from "@/components/Modal";
import Button from "@/components/Button";
import Text from "@/components/Text";
import {
  ProductionScriptResource,
  ProductionVideoReferenceResource,
} from "@/types/resources";
import { api } from "@/utils/api";
import FormField from "@/components/Forms/FormField";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import { FormLayout, Larger } from "@/components/Forms";
import { scrollBarStyles } from "@/utils/theme";

const Heading = styled(Text)`
  margin-bottom: 24px;
`;

const AddLinkButton = styled.button`
  display: inline-flex;
  svg {
    margin-right: 8px;
  }
`;

const AddButtonWrap = styled(Larger)`
  text-align: left;
`;

const StyledFormLayout = styled(FormLayout)`
  gap: 16px;
`;

const FieldsGroup = styled(StyledFormLayout)`
  position: relative;
`;

const RemoveButton = styled(Text)`
  position: absolute;
  top: 0;
  right: 0;
`;

const Dialog = styled(StyledDialog)`
  max-height: 600px;
  overflow-y: auto;
  ${scrollBarStyles};
`;

interface FormDataT {
  name: string;
  video_references: Omit<ProductionVideoReferenceResource, "videoId">[];
}

interface Props {
  onClose: () => void;
  editableScript?: ProductionScriptResource;
}

const ScriptCreationModal: FC<Props> = ({ onClose, editableScript }) => {
  const onCloseWithEventPrevention = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  const { projectId } = useParams();

  const theme = useTheme();

  const alerts = useAlerts();

  const {
    handleSubmit,
    setError,
    control,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: editableScript?.name ?? "",
      video_references: editableScript?.videoReferences?.map(
        ({ videoId, ...r }) => r
      ) ?? [
        {
          name: "",
          url: "",
        },
      ],
    },
  });

  const queryClient = useQueryClient();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "video_references",
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (editableScript) {
        await api.put<typeof data, { id: number }>(
          `/v1/production/scripts/${editableScript.id}`,
          data
        );
      } else {
        const { id: scriptId } = await api.post<typeof data, { id: number }>(
          "/v1/production/scripts",
          {
            ...data,
            production_project_id: projectId,
          }
        );

        await api.post("/v1/production/scenes", {
          name: "My first scene",
          content: "My first scene content",
          production_script_id: scriptId,
        });
      }

      queryClient.invalidateQueries(["production_projects"]);

      onClose();

      alerts.success(
        `Script has been successfully ${
          editableScript ? "updated" : "created"
        }`,
        data.name
      );
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts, true);
    }
  });

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <CloseButton onClick={onCloseWithEventPrevention}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Heading $type="heading2">
          {editableScript ? "Edit" : "Add"} script tab name and video link
        </Heading>
        <form autoComplete="off" onSubmit={onSubmit}>
          <StyledFormLayout>
            <FormField<FormDataT>
              name="name"
              control={control}
              label="Tab name"
            />
            {fields.map((field, index) => (
              <Larger key={field.id}>
                <FieldsGroup>
                  {fields.length > 1 && (
                    <RemoveButton
                      as="button"
                      type="button"
                      $color="error"
                      $type="smallHeadline"
                      onClick={() => remove(index)}
                    >
                      Remove
                    </RemoveButton>
                  )}
                  <FormField<FormDataT>
                    name={`video_references.${index}.url`}
                    inputType="url"
                    control={control}
                    label={`Reference video link ${
                      fields.length > 1 ? index + 1 : ""
                    }`}
                    placeholder="Add link"
                  />
                  <FormField<FormDataT>
                    name={`video_references.${index}.name`}
                    control={control}
                    label={`Reference video name  ${
                      fields.length > 1 ? index + 1 : ""
                    }`}
                    placeholder="Add name"
                  />
                </FieldsGroup>
              </Larger>
            ))}
            <AddButtonWrap>
              <AddLinkButton
                type="button"
                onClick={() =>
                  append({
                    name: "",
                    url: "",
                  })
                }
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="8"
                    fill={theme.colors.purple}
                    fillOpacity="0.12"
                  />
                  <path
                    d="M8 5V11"
                    stroke={theme.colors.purple}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M5 8H11"
                    stroke={theme.colors.purple}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <Text $type="smallHeadline" as="span">
                  Add additional reference link
                </Text>
              </AddLinkButton>
            </AddButtonWrap>
          </StyledFormLayout>
          <Actions>
            <Button
              loading={isSubmitting}
              disabled={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button
              onClick={onCloseWithEventPrevention}
              color="secondary"
              type="button"
            >
              Cancel
            </Button>
          </Actions>
        </form>
      </Dialog>
    </Modal>
  );
};

export default ScriptCreationModal;
